import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { map } from 'rxjs';
import { NgClass } from '@angular/common';

import { SteamApp } from '@app/modules/items/items.entity';
import { ItemExteriorPipe, ItemImagePipe } from '@app/modules/items/pipes';
import { isCsgoItem } from '@app/modules/items/utils';
import { Raffle, TimerRaffleCard } from '@app/modules/raffles/raffles.entity';
import { RafflesState } from '@app/modules/raffles/raffles.state';
import { AuthState } from '@app/modules/users';
import { days, hours } from '@core/common/plurals';
import { addLeadingZero, countdown, indicateLoading } from '@core/helpers';

const MOBILE_DAY = $localize`:@@meta.bonus_wheel.timer.mobile_day:д`;

@Component({
  selector: 'raffle-item-banner',
  templateUrl: './raffle-item.component.html',
  styleUrl: './raffle-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ItemImagePipe,
    ItemExteriorPipe,
    NgClass
  ]
})
export default class RaffleItemBannerComponent {
  public readonly rafflesState = inject(RafflesState);
  public readonly authState = inject(AuthState);
  private readonly destroyRef = inject(DestroyRef);
  public readonly isLoading = signal<boolean>(false);
  public readonly isLoadingUpdate = signal<boolean>(false);
  public raffle!: Raffle;
  public readonly gameMask = signal<string>('');
  public readonly isCsgoItem = isCsgoItem;

  @Input('info')
  set _raffle(raffle: Raffle) {
    this.raffle = raffle;

    if(raffle.prize.item.appId === SteamApp.CSGO) {
      this.gameMask.set('image-mask-cs');
    } else if(raffle.prize.item.appId === SteamApp.DOTA2) {
      this.gameMask.set('image-mask-dota');
    }

    if (raffle) {
      this.timerDataCalculated(raffle.endDateTime);
    }
  }

  public timerData = signal<TimerRaffleCard>({
    days: ['00', 0],
    hours: ['00', 0],
    minutes: ['00', 0],
    seconds: ['00', 0],
    isTimerZero: false
  });

  protected readonly daysPlurals = days;
  protected readonly hoursPlurals = hours;

  updateInfo(): void {
    this.rafflesState.updateRaffleInfo() 
      .pipe(
        indicateLoading(this.isLoadingUpdate),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
  
  private timerDataCalculated(countdownData: string): void {
    let isEnded = false;
  
    const timeShift = this.authState.timeDiff();
    const secondsTotal = dayjs.utc(countdownData).unix() - dayjs.utc(new Date()).unix();

    countdown(countdownData as string, { timeShift, secondsTotal }).pipe(
      map((cd) => {

        const daysLeft = cd.duration!.days();
        const hoursLeft = cd.duration!.hours();
        const minutesLeft = cd.duration!.minutes();
        const secondsLeft = cd.duration!.seconds();
        const isTimerZero = daysLeft === 0 && hoursLeft === 0 && minutesLeft === 0 && secondsLeft === 0;

        this.timerData.set({
          days: [addLeadingZero(daysLeft, false), Number(daysLeft)],
          hours: [addLeadingZero(hoursLeft, false), Number(hoursLeft)],
          minutes: [addLeadingZero(minutesLeft, false), Number(minutesLeft)],
          seconds: [addLeadingZero(secondsLeft, false), Number(secondsLeft)],
          isTimerZero
        });

        isEnded = cd.progress === 100 && isTimerZero;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  daysCounter(days: string | number): string {
    const numericDays = typeof days === 'string' ? parseInt(days, 10) : days;

    if (numericDays > 1) {
      return numericDays + MOBILE_DAY;
    }

    return numericDays + MOBILE_DAY;
  }
}